import { Fragment, useEffect, useMemo, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, Info } from 'lucide-react';
import { nextSort, type SortState } from '../ui/SortableHeader';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import type { ActualsResponse, GroupActuals, KpiCatalogueEntry, OpenEvidence, EvidenceScopeType } from '../../types/strategicKpis';
import { CATEGORY_LABEL, CATEGORY_ORDER, formatKpiValue, judgedOnYtdShare, kpiLabel, noDataText, paceBasisText } from '../../utils/strategicKpiFormat';
import { judgeGroup, leaderboardRows, lightOf, paceAtYearEnd, sortByAchievement, type JudgedValue, type LeaderboardRow } from '../../utils/strategicKpiSelectors';
import { BulletBar, BulletLegend } from './BulletBar';
import { StatusBadge, statusText } from './parts';

export type GroupBy = 'none' | 'team' | 'company';
export const GROUP_BY: readonly GroupBy[] = ['none', 'team', 'company'];
const GROUP_LABEL: Record<GroupBy, string> = { none: 'None', team: 'Team', company: 'Company' };

interface Props {
    data: ActualsResponse;
    steered: KpiCatalogueEntry[];
    onOpenEvidence: OpenEvidence;
    /** K5 (S3-02): the ranked KPI and the grouping live in the page URL; null = the default KPI. */
    kpiKey: string | null;
    onKpiKey: (kpiKey: string) => void;
    groupBy: GroupBy;
    onGroupBy: (g: GroupBy) => void;
    /** B3: free-text filter on the person's name; the rank number stays the rank in the whole list. */
    search?: string;
    /** B3: a click on a person row opens the person panel (E16). Without it the rows are not clickable. */
    onOpenPerson?: (staffId: string) => void;
    /** The person whose panel is open — the row is drawn selected. */
    selectedStaffId?: string | null;
    /** Rendered at the start / end of the one toolbar row (PeopleTab: the view segment, the search). */
    toolbarStart?: ReactNode;
    toolbarEnd?: ReactNode;
}

type SortKey = 'person' | 'actual' | 'target' | 'achievement';

const COLUMNS = [
    { key: 'rank', width: 56 }, { key: 'person', width: 240 }, { key: 'actual', width: 120 }, { key: 'target', width: 150 },
    { key: 'bar', width: 190 }, { key: 'achievement', width: 170 },
];
export const DEFAULT_KPI = 'visits.target_visits_ytd';

/** BAB lives on company and BU rows only (E5, E18): no person ever has a value, so it is never ranked. */
export function rankableKpis(steered: KpiCatalogueEntry[]): KpiCatalogueEntry[] {
    return steered.filter(k => k.category !== 'bab');
}

/** The KPI the ranking shows: the linked one if it is rankable here, else the default, else the first one. */
export function rankingKpi(steered: KpiCatalogueEntry[], kpiKey: string | null): KpiCatalogueEntry | undefined {
    const list = rankableKpis(steered);
    return list.find(k => k.kpiKey === kpiKey) ?? list.find(k => k.kpiKey === DEFAULT_KPI) ?? list[0];
}

// The data-skpi-* contract (context/reports/2026-10-skpi-redesign/A0/method.md): the texts exactly as the
// ranking before B3 displayed them, from the same formatters, so scripts/skpi-compare.mjs can hold old = new.

/** The actual as displayed: «no data» for a null actual (the reason is in the tooltip), else formatKpiValue. */
export function rankingValueText(kpi: Pick<KpiCatalogueEntry, 'unit'>, j: Pick<JudgedValue, 'actual'>): string {
    return j.actual === null ? 'no data' : formatKpiValue(kpi.unit, j.actual);
}

/** The target as displayed: visits and GP amounts «YTD share · year», every other KPI the year target. */
export function rankingTargetText(kpi: Pick<KpiCatalogueEntry, 'unit' | 'category' | 'targetProrated'>, j: Pick<JudgedValue, 'target' | 'targetYtd'>): string {
    return judgedOnYtdShare(kpi)
        ? `${formatKpiValue(kpi.unit, j.targetYtd)} · ${formatKpiValue(kpi.unit, j.target)}`
        : formatKpiValue(kpi.unit, j.target);
}

/**
 * The ranking of People (B3): one KPI, every person with a stored target for it, as a sorted list of
 * bullets on one common scale: behind first by default, optionally grouped by team or company with the
 * group's own engine pace in the header (groups in the order of that pace, so the teams behind plan come
 * first — what the AttentionList's team exceptions said). Persons without a target for the KPI are counted
 * above the table, never listed with zeros. The selection and sorting logic is the one from before the
 * redesign (masterplan §2 rule 7); only the drawing moved to the kit. One table, the last block of the tab.
 */
export function Leaderboard({ data, steered, onOpenEvidence, kpiKey, onKpiKey, groupBy, onGroupBy, search = '', onOpenPerson, selectedStaffId = null, toolbarStart, toolbarEnd }: Props) {
    const [sort, setSort] = useState<SortState<SortKey>>({ key: 'achievement', direction: 'asc' });
    const { widthOf, resizingKey, startResize, resetWidth, shouldSuppressClick } = useResizableColumns(COLUMNS, 'strategic-kpis-leaderboard-col-widths');

    const rankable = useMemo(() => rankableKpis(steered), [steered]);
    const kpi = rankingKpi(steered, kpiKey);
    const atYearEnd = kpi ? paceAtYearEnd(kpi, data.windows.yearFraction) : false;
    // A linked KPI that is not rankable here (another BU or year) is replaced in the link too, as the state was before K5.
    useEffect(() => {
        if (kpiKey !== null && kpi && kpi.kpiKey !== kpiKey) onKpiKey(kpi.kpiKey);
    }, [kpiKey, kpi, onKpiKey]);

    const board = useMemo(() => kpi ? leaderboardRows(data, kpi.kpiKey) : { rows: [], withoutTarget: data.persons.length, scaleMax: 0 }, [data, kpi]);

    const sorted = useMemo(() => {
        if (sort.key === 'achievement' || sort.key === null) return sortByAchievement(board.rows, sort.direction);
        const val = (r: LeaderboardRow): string | number | null => sort.key === 'person' ? r.person.fullName
            : sort.key === 'actual' ? r.judged.actual
            : (r.judged.targetYtd ?? r.judged.target);
        return [...board.rows].sort((a, b) => {
            const av = val(a), bv = val(b);
            const aNil = av === null || av === '', bNil = bv === null || bv === '';
            if (aNil && bNil) return 0;
            if (aNil) return 1;
            if (bNil) return -1;
            const base = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv), undefined, { numeric: true });
            return base * (sort.direction === 'asc' ? 1 : -1);
        });
    }, [board.rows, sort]);

    const groups = useMemo(() => {
        if (groupBy === 'none') return [{ key: '', group: null as GroupActuals | null, label: '', rows: sorted }];
        const m = new Map<string, { key: string; group: GroupActuals | null; label: string; rows: LeaderboardRow[] }>();
        for (const r of sorted) {
            const g = groupBy === 'team' ? r.team : r.company;
            const key = groupBy === 'team' ? (r.person.teamManagerStaffId ?? '') : r.person.companyCode;
            const label = groupBy === 'team' ? (g?.label ?? r.person.teamManagerName ?? 'no team') : r.person.companyCode;
            const e = m.get(key) ?? { key, group: g, label, rows: [] };
            e.rows.push(r);
            m.set(key, e);
        }
        // Groups in the order of their own pace (behind first / ahead first like the rows), nameless last.
        const list = [...m.values()];
        const pace = (e: typeof list[number]) => kpi && e.group ? judgeGroup(kpi, e.group.values[kpi.kpiKey]).achievement : null;
        return sortByAchievement(list.map(e => ({ ...e, judged: { achievement: pace(e) } })), sort.key === 'achievement' ? sort.direction : 'asc');
    }, [sorted, groupBy, kpi, sort]);

    // The rank is the position in the display order over all groups; the search hides rows, never renumbers them.
    const q = search.trim().toLocaleLowerCase();
    const shown = useMemo(() => {
        let rank = 0;
        return groups.map(g => ({
            ...g,
            rows: g.rows.map(r => ({ row: r, rank: ++rank })).filter(x => !q || x.row.person.fullName.toLocaleLowerCase().includes(q)),
        }));
    }, [groups, q]);
    const shownCount = shown.reduce((s, g) => s + g.rows.length, 0);

    const onSort = (key: SortKey) => { if (shouldSuppressClick()) return; setSort(s => nextSort(s, key, key === 'actual' || key === 'target' || key === 'achievement')); };
    const isVisits = kpi?.category === 'visits';
    const ytdShare = kpi ? judgedOnYtdShare(kpi) : false;
    const tableWidth = COLUMNS.reduce((s, c) => s + widthOf(c.key), 0);

    const toolbar = (
        <div className="toolbar">
            {toolbarStart}
            {kpi && <KpiPicker kpis={rankable} value={kpi.kpiKey} onChange={onKpiKey} />}
            {kpi && (
                <>
                    <span className="lbl" id="skpi-ranking-group-label">Group</span>
                    <div className="seg" role="group" aria-labelledby="skpi-ranking-group-label">
                        {GROUP_BY.map(g => (
                            <button key={g} type="button" onClick={() => onGroupBy(g)} aria-pressed={groupBy === g}>{GROUP_LABEL[g]}</button>
                        ))}
                    </div>
                </>
            )}
            <span className="tb-grow" />
            {toolbarEnd}
        </div>
    );

    if (!kpi) {
        return (
            <div className="space-y-4">
                {toolbar}
                <div className="empty">
                    <b>No ranking</b>
                    <p>{steered.length > 0
                        ? `Only BAB has targets for ${data.year}. BAB sits on company and BU rows, not on persons, so it cannot be ranked.`
                        : `No KPI has a target for ${data.year} — the ranking needs one.`}</p>
                </div>
            </div>
        );
    }

    const head = (label: string, key: SortKey, numeric: boolean) => {
        const active = sort.key === key;
        return (
            <th className={numeric ? 'n' : undefined} aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}>
                <button type="button" className="sort" onClick={() => onSort(key)}>
                    {label}{active && <span className="caret" aria-hidden="true">{sort.direction === 'asc' ? '▲' : '▼'}</span>}
                </button>
                <ResizeHandle label={label} active={resizingKey === key} onStart={x => startResize(key, x)} onReset={() => resetWidth(key)} />
            </th>
        );
    };

    return (
        <div className="space-y-4">
            {toolbar}
            <div className="tbl-foot">
                <span>
                    {board.rows.length} persons with a target for <span className="strong">{kpiLabel(kpi)}</span> · {board.withoutTarget} without one are not listed
                    {q && <> · {shownCount} match «{search.trim()}»</>}
                    {ytdShare && <> · targets prorated by {Math.round(data.windows.yearFraction * 100)} % of the year</>} · {paceBasisText(kpi)}{kpi.direction === 'lower' && ' · lower is better'}{atYearEnd && ' · judged at the year end, no light until then'}
                </span>
                <BulletLegend ytdShare={ytdShare ? '' : false} planned={isVisits} />
            </div>
            <div className={`tbl-wrap${resizingKey ? ' resizing' : ''}`} data-skpi-ranking-kpi={kpi.kpiKey} data-testid="skpi-ranking">
                <table className="tbl tbl-fixed" style={{ width: tableWidth, minWidth: '100%' }}>
                    <colgroup>{COLUMNS.map(c => <col key={c.key} style={{ width: widthOf(c.key) }} />)}</colgroup>
                    <thead>
                        <tr>
                            <th className="n"><span className="sr-only">Rank</span><span aria-hidden="true">#</span></th>
                            {head('Person', 'person', false)}
                            {head(kpi.unit === 'amount' ? 'Actual (EUR)' : 'Actual', 'actual', true)}
                            {head(ytdShare ? 'YTD target · year' : 'Target', 'target', true)}
                            <th>Actual vs {ytdShare ? 'YTD target' : 'year target'}</th>
                            {head('On course?', 'achievement', false)}
                        </tr>
                    </thead>
                    <tbody>
                        {board.rows.length === 0 && (
                            <tr><td colSpan={COLUMNS.length} className="dash">Nobody has a target for {kpi.name} in {data.year}.</td></tr>
                        )}
                        {board.rows.length > 0 && shownCount === 0 && (
                            <tr><td colSpan={COLUMNS.length} className="dash">No person with a target matches «{search.trim()}».</td></tr>
                        )}
                        {shown.map(g => (
                            <Fragment key={g.key}>
                                {groupBy !== 'none' && g.rows.length > 0 && (
                                    <GroupHeader label={g.label} count={g.rows.length} kpi={kpi} group={g.group} atYearEnd={atYearEnd} onOpen={g.group ? () => onOpenEvidence(kpi.kpiKey, g.group!.scopeType as EvidenceScopeType, g.group!.scopeKey, `${g.group!.label} (${g.group!.scopeType === 'TEAM' ? 'Team' : 'Company'})`, g.group!.partial) : undefined} />
                                )}
                                {g.rows.map(({ row: r, rank }) => (
                                    <PersonRow
                                        key={r.person.staffId}
                                        row={r}
                                        rank={rank}
                                        kpi={kpi}
                                        scaleMax={board.scaleMax}
                                        isVisits={isVisits}
                                        ytdShare={ytdShare}
                                        atYearEnd={atYearEnd}
                                        selected={selectedStaffId === r.person.staffId}
                                        onOpen={() => onOpenEvidence(kpi.kpiKey, 'PERSON', r.person.staffId, r.person.fullName)}
                                        onOpenPerson={onOpenPerson ? () => onOpenPerson(r.person.staffId) : undefined}
                                    />
                                ))}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/** The KPI of the ranking: a native select in the kit's control, the steered KPIs under their family. */
function KpiPicker({ kpis, value, onChange }: { kpis: KpiCatalogueEntry[]; value: string; onChange: (kpiKey: string) => void }) {
    const families = [...CATEGORY_ORDER, ...[...new Set(kpis.map(k => k.category))].filter(c => !CATEGORY_ORDER.includes(c))];
    const current = kpis.find(k => k.kpiKey === value);
    return (
        <>
            <label className="lbl" htmlFor="skpi-ranking-kpi">KPI</label>
            <span className="relative inline-flex items-center">
                <select
                    id="skpi-ranking-kpi"
                    className="select"
                    style={{ paddingRight: 32 } as CSSProperties}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    title={current?.definitionText}
                    data-testid="skpi-ranking-kpi-select"
                >
                    {families.map(f => {
                        const list = kpis.filter(k => k.category === f);
                        if (list.length === 0) return null;
                        return (
                            <optgroup key={f} label={CATEGORY_LABEL[f] ?? f}>
                                {list.map(k => <option key={k.kpiKey} value={k.kpiKey}>{kpiLabel(k)}</option>)}
                            </optgroup>
                        );
                    })}
                </select>
                <ChevronDown className="i sm pointer-events-none absolute right-2.5" aria-hidden="true" />
            </span>
        </>
    );
}

/** The drag strip of a header cell (kit `.col-resize`); arrow keys resize, Enter resets (as ui/ColumnResizeHandle). */
function ResizeHandle({ label, active, onStart, onReset }: { label: string; active: boolean; onStart: (clientX: number) => void; onReset: () => void }) {
    const onKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            const delta = (e.key === 'ArrowRight' ? 1 : -1) * (e.shiftKey ? 50 : 10);
            onStart(0);
            setTimeout(() => {
                window.dispatchEvent(new MouseEvent('mousemove', { clientX: delta }));
                window.dispatchEvent(new MouseEvent('mouseup'));
            }, 0);
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onReset();
        }
    };
    return (
        <span
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${label}`}
            tabIndex={0}
            className="col-resize"
            data-active={active || undefined}
            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onStart(e.clientX); }}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => { e.stopPropagation(); onReset(); }}
            onKeyDown={onKeyDown}
            title="Drag to resize · double-click or Enter to reset · arrow keys when focused"
        />
    );
}

/** A figure that opens its evidence (E9): the number itself is the button. */
function FigureButton({ text, name, onClick, noData }: { text: string; name: string; onClick: () => void; noData?: string }) {
    return (
        <button
            type="button"
            className="lnk tnum"
            style={{ fontSize: 'inherit' }}
            onClick={e => { e.stopPropagation(); onClick(); }}
            aria-label={`${text} — ${name}`}
            title={noData ? `${noDataText(noData)} — ${name}` : name}
        >
            {text}{noData && <Info className="i sm ml-1" aria-hidden="true" />}
        </button>
    );
}

function GroupHeader({ label, count, kpi, group, atYearEnd, onOpen }: { label: string; count: number; kpi: KpiCatalogueEntry; group: GroupActuals | null; atYearEnd: boolean; onOpen?: () => void }) {
    const j = group ? judgeGroup(kpi, group.values[kpi.kpiKey]) : null;
    const light = j ? lightOf(j) : 'none';
    const actual = j && j.actual !== null ? formatKpiValue(kpi.unit, j.actual) : '';
    return (
        <tr className="grp" data-row-kind="group">
            <td colSpan={2}>{label}<span className="gsub">{count} with a target{j && j.hasTarget ? ` · ${j.hasGroupTarget ? 'group target' : 'Σ person targets'}` : ''}</span></td>
            <td className="n">{onOpen ? <FigureButton text={actual || 'no data'} name={`Rows behind ${kpiLabel(kpi)} of ${label}`} onClick={onOpen} /> : actual}</td>
            <td className="n">{j && j.hasTarget ? formatKpiValue(kpi.unit, judgedOnYtdShare(kpi) ? j.targetYtd : j.target) : ''}</td>
            <td />
            <td>{j && j.hasTarget && <StatusBadge light={light} achievement={j.achievement} noData={j.noData} atYearEnd={atYearEnd} />}</td>
        </tr>
    );
}

function PersonRow({ row: r, rank, kpi, scaleMax, isVisits, ytdShare, atYearEnd, selected, onOpen, onOpenPerson }: {
    row: LeaderboardRow;
    rank: number;
    kpi: KpiCatalogueEntry;
    scaleMax: number;
    isVisits: boolean;
    ytdShare: boolean;
    atYearEnd: boolean;
    selected: boolean;
    onOpen: () => void;
    onOpenPerson?: () => void;
}) {
    const j = r.judged;
    const light = lightOf(j);
    const value = rankingValueText(kpi, j);
    const name = `Rows behind ${kpiLabel(kpi)} of ${r.person.fullName}`;
    const onKeyDown = onOpenPerson ? (e: KeyboardEvent<HTMLTableRowElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenPerson(); }
    } : undefined;
    return (
        <tr
            className={selected ? 'selected' : undefined}
            data-row-kind="person"
            data-scope={`PERSON:${r.person.staffId}`}
            data-skpi-person={r.person.staffId}
            data-skpi-kpi={kpi.kpiKey}
            data-skpi-value={value}
            data-skpi-target={rankingTargetText(kpi, j)}
            data-skpi-status={statusText({ light, achievement: j.achievement, atYearEnd })}
            tabIndex={onOpenPerson ? 0 : undefined}
            onClick={onOpenPerson ? () => { if (window.getSelection()?.toString()) return; onOpenPerson(); } : undefined}
            onKeyDown={onKeyDown}
            style={onOpenPerson ? { cursor: 'pointer' } : undefined}
        >
            <td className="n"><span className="muted">{rank}</span></td>
            <td title={`${r.person.fullName} · ${r.person.companyCode} · StaffID ${r.person.staffId}${onOpenPerson ? ' · open the person' : ''}`}>
                <div className="cellstack">
                    <span className="name">{r.person.fullName}{!r.person.isActive && <span className="muted"> (left)</span>}</span>
                    <span className="sub2">{r.person.teamManagerName ?? 'no team'} · {r.person.companyCode}</span>
                </div>
            </td>
            <td className="n">
                <div className="cellstack">
                    <span><FigureButton text={value} name={name} onClick={onOpen} noData={j.actual === null ? j.noData : undefined} /></span>
                    {isVisits && j.planned !== undefined && j.planned > 0 && <span className="sub2">+ {j.planned} planned</span>}
                </div>
            </td>
            <td className="n">
                {ytdShare ? <>{formatKpiValue(kpi.unit, j.targetYtd)} <span className="muted">· {formatKpiValue(kpi.unit, j.target)}</span></> : formatKpiValue(kpi.unit, j.target)}
            </td>
            <td><BulletBar unit={kpi.unit} actual={j.actual} planned={j.planned} prior={j.prior} targetYtd={j.targetYtd} targetYear={j.target} ytdShare={ytdShare} lowerIsBetter={kpi.direction === 'lower'} scaleMax={scaleMax} width="100%" height={14} /></td>
            <td><StatusBadge light={light} achievement={j.achievement} noData={j.noData} atYearEnd={atYearEnd} /></td>
        </tr>
    );
}
